import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAccountNotifications } from "../hooks/useAccountNotifications.js";
import { useWallet } from "../context/WalletContext.jsx";
import { chainNowMs } from "../utils/chainClock.js";

/**
 * Header notification bell.
 *
 * Per client (Jun 2026): the bell shows ONLY notifications about the
 * connected wallet's own positions — Auto-Reinvest minting a new TD, a TD
 * crossing its 1-/2-year mark, a TD maturing — never protocol-health /
 * system alerts. Those system alerts still drive the PausedDepositsBanner;
 * they just don't pop the bell, so we never "unnecessarily worry people".
 *
 * Click → dropdown of the 8 most recent account notifications. "Dismiss"
 * is a LOCAL action (these are derived, not backend rows) persisted in
 * localStorage via `useAccountNotifications.dismiss`.
 *
 * Closes on outside click + Escape — same pattern as the Sort/Filter
 * popovers used elsewhere in the app so the chrome behaves consistently.
 */
export default function NotificationBell() {
    const { address } = useWallet();
    const { items, unreadCount, isLoading, dismiss } = useAccountNotifications(address);
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (event) => {
            if (!wrapperRef.current) return;
            if (!wrapperRef.current.contains(event.target)) setOpen(false);
        };
        const onKey = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    // 99+ cap so a noisy run doesn't push the badge wider than the
    // button outline — the dropdown shows the precise list anyway.
    const badgeText = unreadCount === 0 ? null : unreadCount > 99 ? "99+" : String(unreadCount);
    // Account notifications are informational/positive (new TD, milestone,
    // maturity) — never alarming — so the badge is always the gold accent,
    // never the red "critical" colour.
    const badgeClass = "bg-[#FBC206] text-[#10281b]";

    return (
        <div ref={wrapperRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={`Notifications${badgeText ? ` (${badgeText} unread)` : ""}`}
                className="relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-[1.5px] ring-[#1D3E29] text-[#1D3E29] hover:bg-white/40"
            >
                <svg
                    className="h-3.5 w-3.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <path d="M6 8a6 6 0 0 1 12 0v5l1.5 3h-15L6 13z" />
                    <path d="M10 19a2 2 0 0 0 4 0" />
                </svg>
                {badgeText ? (
                    <span
                        className={`absolute -right-1 -top-1.5 inline-flex h-3.5 min-w-[14px] items-center justify-center rounded-full px-1 text-[9px] font-bold ${badgeClass}`}
                    >
                        {badgeText}
                    </span>
                ) : null}
            </button>

            {open ? (
                <div
                    role="menu"
                    aria-label="Open notifications"
                    className="absolute right-0 top-full z-[60] mt-2 w-80 overflow-hidden rounded-[12px] bg-white ring-1 ring-[#bfb67b] shadow-xl"
                >
                    <div className="border-b border-[#1D3E29]/10 bg-[#1D3E29] px-4 py-2.5">
                        <div className="text-[9px] font-semibold uppercase tracking-widest text-[#FBC206]">
                            Notifications
                        </div>
                        <div className="mt-0.5 font-manrope text-[12px] font-bold text-white">
                            {isLoading
                                ? "Loading…"
                                : unreadCount === 0
                                    ? "You're all caught up"
                                    : `${unreadCount} update${unreadCount === 1 ? "" : "s"}`}
                        </div>
                    </div>

                    <AlertList items={items} onResolve={dismiss} />

                    <div className="border-t border-[#1D3E29]/10 bg-[#f7f8f3] px-4 py-2 text-right">
                        <Link
                            to="/term-deposits"
                            onClick={() => setOpen(false)}
                            className="text-[10.5px] font-semibold text-[#1D3E29] hover:underline"
                        >
                            View my Term Deposits →
                        </Link>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function AlertList({ items, onResolve }) {
    // Cap to 8 rows — anything more and the dropdown pushes off-screen
    // on small viewports. The "Open Protocol Health" link below covers
    // the full list.
    const visible = items.slice(0, 8);
    if (visible.length === 0) {
        return (
            <div className="px-4 py-6 text-center text-[11px] text-[#1D3E29]/60">
                Nothing requires your attention right now.
            </div>
        );
    }
    return (
        <ul className="max-h-80 divide-y divide-[#1D3E29]/10 overflow-y-auto">
            {visible.map((alert) => (
                <li key={alert._id} className="flex items-start gap-2 px-3 py-2.5 hover:bg-[#1D3E29]/5">
                    <SeverityDot severity={alert.severity} />
                    <div className="min-w-0 flex-1">
                        <div className="text-[11.5px] font-semibold text-[#1D3E29] leading-tight">
                            {alert.message || humanType(alert.type)}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1.5 text-[9.5px] text-[#1D3E29]/60">
                            <span className="font-mono">{alert.type}</span>
                            <span>·</span>
                            <span>{relativeTime(alert.createdAt)}</span>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => onResolve(alert._id)}
                        className="shrink-0 rounded-full bg-[#1D3E29]/10 px-2 py-0.5 text-[9.5px] font-semibold text-[#1D3E29] hover:bg-[#1D3E29]/20"
                    >
                        Dismiss
                    </button>
                </li>
            ))}
        </ul>
    );
}

function SeverityDot({ severity }) {
    const color = severity === "critical"
        ? "bg-[#FF4444]"
        : severity === "warning"
            ? "bg-[#FBC206]"
            : "bg-[#1D3E29]/40";
    return (
        <span
            className={`mt-1 inline-flex h-2 w-2 shrink-0 rounded-full ${color}`}
            aria-label={`Severity: ${severity}`}
        />
    );
}

/**
 * Falls back to a humanised version of the alert `type` when the
 * backend payload doesn't carry an explicit `message`. Mirrors the
 * Recent Transactions card pattern in Dashboard.jsx so the wording
 * stays consistent across surfaces.
 */
function humanType(type) {
    if (!type) return "Alert";
    return type
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function relativeTime(iso) {
    if (!iso) return "";
    // alert.createdAt marks a CHAIN event; measure "X ago" against chain
    // time (not wall-clock) so it stays correct under local time-warp.
    const ms = chainNowMs() - new Date(iso).getTime();
    if (ms < 0) return "just now";
    const min = Math.floor(ms / 60_000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}d ago`;
    const mo = Math.floor(day / 30);
    return `${mo}mo ago`;
}
